/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { CellPadding, TableFooterRendererContext } from '#UNSAFE_TableView';

import { FormContext } from '#hooks/UNSAFE_useFormContext';
import { classNames } from '#utils/UNSAFE_classNames';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { preventBrowserTooltipProps } from '#PRIVATE_TableCommon/utils/TooltipUtils';
import { getFormTextAlign, getHeaderFooterCellClassArray } from './utils/TableThemeUtils';

/**
 * Props for the TableFooterCell Component
 */
type TableFooterCellProps<C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSelected: boolean;
  isNextColumnSelected: boolean;
  isPreviousColumnSelected: boolean;
  isSticky: boolean;
  isColumnSelectable: boolean;
  isShowFocusRing: boolean;
  hasTabIndex: boolean;
  isTabbableMode: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isActiveStyle: boolean;
  isHoverStyle: boolean;
  isPreviousCellActiveStyle: boolean;
  isPreviousCellHoverStyle: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  footerText?: string;
  stickyBottomOffset?: number;
  focusTarget?: 'cell' | 'content';
  renderer?: (context: TableFooterRendererContext<C>) => ComponentChildren;
  padding?: CellPadding;
  tooltip?: 'enabled' | 'disabled';
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

function _defaultFooterCellRenderer<C>({ footerText }: TableFooterRendererContext<C>) {
  return footerText != null ? footerText : undefined;
}

/**
 * The internal component used to render a single cell in a TableFooterRow.
 */
export function TableFooterCell<C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  isSelected,
  isNextColumnSelected,
  isPreviousColumnSelected,
  isSticky,
  isColumnSelectable,
  isShowFocusRing,
  hasTabIndex,
  isTabbableMode,
  stickyBottomOffset = 0,
  focusTarget = 'cell',
  footerText,
  renderer = _defaultFooterCellRenderer,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isActiveStyle,
  isHoverStyle,
  isPreviousCellActiveStyle,
  isPreviousCellHoverStyle,
  isRtl,
  isHighContrast,
  padding,
  tooltip,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableFooterCellProps<C>) {
  const browser = getClientHints().browser;
  const supportsDefaultTooltip = tooltip !== 'disabled';
  const supportsAutoFocus = focusTarget === 'content';

  const cellClasses = classNames(
    getHeaderFooterCellClassArray({
      isHeader: false,
      isActiveStyle,
      isHoverStyle,
      isPreviousCellActiveStyle,
      isPreviousCellHoverStyle,
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      hasVerticalGridlines,
      isSelected,
      isNextColumnSelected,
      isPreviousColumnSelected,
      isSticky,
      hasStartEdge,
      hasEndEdge,
      isShowFocusRing,
      isStickyStartEdge,
      isStickyEndEdge,
      isRtl,
      horizontalAlignment,
      verticalAlignment,
      isHighContrast,
      padding
    })
  );

  const footerCellRendererProps = {
    key: columnKey,
    footerText: footerText,
    isTabbable: isTabbableMode
  };

  return (
    <td
      aria-colindex={columnIndex + 1}
      {...(isSelected
        ? { 'aria-selected': true }
        : isColumnSelectable
        ? { 'aria-selected': false }
        : {})}
      class={cellClasses}
      style={`bottom:${stickyBottomOffset}px;`}
      tabIndex={hasTabIndex ? 0 : -1}
      role={'gridcell'}
      data-oj-cell-type={'footer'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-footer={tableId}
      data-oj-table-tooltip={supportsDefaultTooltip ? 'enabled' : undefined}
      data-oj-table-auto-focus={supportsAutoFocus ? 'enabled' : undefined}
      {...(supportsDefaultTooltip && browser === 'safari' ? preventBrowserTooltipProps : {})}>
      <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
        {renderer(footerCellRendererProps)}
      </FormContext.Provider>
    </td>
  );
}
